"use client";

import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import swal from "sweetalert";
import { io } from "socket.io-client";

import { useModal } from "../providers/modal-provider.js";
import LiveTrackingMap from "./LiveTrackingMap";

export default function TrackingModal() {
    const { displayModal, modalData, closeModal, setModalView, setModalData } = useModal();

    const [eta, setEta] = useState("Calculating…");
    const [distance, setDistance] = useState("--");
    const [driverLocation, setDriverLocation] = useState(null);
    const [path, setPath] = useState([]);
    const socketRef = useRef(null);

    /* ------------------------------------------------------------------
     * GET partner ID depending on modal type
     * ------------------------------------------------------------------*/
    let partnerId = null;

    if (modalData?.type === "order") {
        partnerId = modalData?.deliveryOrder?.delivery_partner_id;
    }

    if (modalData?.type === "partner") {
        partnerId = modalData?.partner_id;
    }

    /* ------------------------------------------------------------------
     * SOCKET CONNECTION
     * ------------------------------------------------------------------*/
    useEffect(() => {
        if (!partnerId) return;

        const socket = io(process.env.REACT_APP_SOCKET_SERVER_URL, {
            transports: ["websocket"],
            forceNew: true,
        });

        socketRef.current = socket;

        socket.on("connect", () => {
            socket.emit("join_partner_tracking", {
                delivery_partner_id: String(partnerId),
            });
        });

        socket.on("order_location_update", (loc) => {
            const next = { lat: Number(loc.lat), lng: Number(loc.lng) };
            setDriverLocation(next);

            setPath((prev) => {
                const last = prev[prev.length - 1];
                if (!last || last.lat !== next.lat || last.lng !== next.lng) {
                    return [...prev, next];
                }
                return prev;
            });
        });

        return () => {
            socket.disconnect();
        };
    }, [partnerId]);

    /* ------------------------------------------------------------------
     * GET CUSTOMER LOCATION IF ORDER TYPE
     * For partner tracking we do not need customer location
     * ------------------------------------------------------------------*/
    let customerLocation = null;

    if (modalData?.type === "order") {
        const order = modalData?.deliveryOrder;

        customerLocation = order?.order_data?.shipping_address
            ? {
                lat: Number(order.order_data.shipping_address.shipping_map_coordinates.lat),
                lng: Number(order.order_data.shipping_address.shipping_map_coordinates.lng),
            }
            : null;
    }

    /* ------------------------------------------------------------------
     * CLOSE MODAL
     * ------------------------------------------------------------------*/
    const handleTrackingModalClose = () => {
        setEta("Calculating…");
        setDistance("--");
        setDriverLocation(null);
        setPath([]);

        closeModal();
        setModalView(null);
        setModalData(null);
    };

    /* ------------------------------------------------------------------
     * TITLE LOGIC
     * ------------------------------------------------------------------*/
    const title =
        modalData?.type === "order"
            ? `Live Tracking – Order #${modalData?.deliveryOrder?.order_number}`
            : `Live Partner Tracking – ${modalData?.partner_name}`;

    return (
        <Modal show={displayModal} onHide={handleTrackingModalClose} size="lg" centered>
            {/* HEADER */}
            <Modal.Header className="py-2">
                <Modal.Title>{title}</Modal.Title>

                <div
                    className="btn btn-sm btn-icon btn-active-color-primary"
                    onClick={handleTrackingModalClose}
                >
                    <span className="svg-icon svg-icon-1">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24px"
                            height="24px"
                            viewBox="0 0 24 24"
                        >
                            <g
                                transform="translate(12 12) rotate(-45) translate(-12 -12) translate(4 4)"
                                fill="#000"
                            >
                                <rect x="0" y="7" width="16" height="2" rx="1" />
                                <rect
                                    opacity="0.5"
                                    transform="translate(8 8) rotate(-270) translate(-8 -8)"
                                    x="0"
                                    y="7"
                                    width="16"
                                    height="2"
                                    rx="1"
                                />
                            </g>
                        </svg>
                    </span>
                </div>
            </Modal.Header>

            {/* BODY */}
            <Modal.Body>
                <div className="container">

                    {/* INFO */}
                    <div className="mb-3">

                        {modalData?.type === "order" && (
                            <div className="fw-bold fs-5 mb-2">
                                Delivery Order: {modalData?.deliveryOrder?.order_number}
                            </div>
                        )}

                        <div className="row g-3">

                            {/* LEFT SIDE: Partner Details OR Partner Name */}
                            <div className="col-md-6">
                                {modalData?.type === "order" ? (
                                    <div className="p-2 border rounded">
                                        <div className="fw-bold mb-1">Partner Details</div>
                                        <div>👤 {modalData?.deliveryOrder?.delivery_partner_name || "N/A"}</div>
                                        <div>📞 {modalData?.deliveryOrder?.delivery_partner_phone || "N/A"}</div>
                                    </div>
                                ) : (
                                    <div className="p-2 border rounded">
                                        <div className="fw-bold fs-5">Partner: {modalData?.partner_name}</div>
                                    </div>
                                )}
                            </div>

                            {/* RIGHT SIDE: DRIVER STATUS */}
                            <div className="col-md-6">
                                <div className="p-2 border rounded">
                                    {driverLocation ? (
                                        modalData?.type === "order" && (
                                            <>
                                                <div>🛵 Driver On The Way</div>
                                                <div>📍 Distance: {distance}</div>
                                                <div>⏳ ETA: {eta}</div>
                                            </>
                                        )
                                    ) : (
                                        <div className="text-muted">Waiting for live location…</div>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>


                    {/* MAP */}
                    <div className="w-100" style={{ height: "65vh" }}>
                        <LiveTrackingMap
                            driverLocation={driverLocation}
                            customerLocation={customerLocation} // null for partner
                            path={path}
                            onETAUpdate={(etaValue, distValue) => {
                                setEta(etaValue);
                                setDistance(distValue);
                            }}
                        />
                    </div>
                </div>
            </Modal.Body>

            {/* FOOTER */}
            <Modal.Footer>
                <button
                    className="btn btn-danger rounded"
                    onClick={handleTrackingModalClose}
                >
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}
